import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { isNullOrUndefined } from 'util';
import { LIGHTNING_FORECAST_LEGEND_LIST, LIGHTNING_LAYER_LIST, LOCATION_HIE, LightningMapConstants } from './lightning-map-constants';
import { environment } from 'src/environments/environment';
import { AgricultureMapConstants, LAYER_TYPES } from 'src/app/main/constants/dims-map-constants';
import { HttpClient } from '@angular/common/http';
import * as L from "leaflet";
import 'src/assets/leaflet-image-transform/L.ImageTransform.js';
import * as R from 'leaflet-responsive-popup'
import { LightningDashboardService } from '../lightning-dashboard.service';
import { ProjConstants } from 'src/app/utils/proj.constant';
import { MapConstants } from 'src/app/utils/map.constant';


@Injectable({
  providedIn: 'root'
})
export class LightningMapService {
  gisMapComp: any;
  markerLayer = new L.LayerGroup();
  cachedLightningData: any = null;
  lightningHeatMapData: any = null;
  miniChartData: any = null;
  miniChartDataField: 'categoryAreaPercentage' | 'categoryCount' = 'categoryAreaPercentage';
  private observedHeatMapDate: string | null = null;

  // ── New: time-block based observed state ────────────────────────────────
  hourlyTimeBlocks: number[] = [];
  selectedHourlyTimestamp: number | null = null;
  private cachedObservedHeatMapData: any = null;

  /** Emits the new hourly timestamp whenever the play bar moves in observed mode */
  observedHourChanged$ = new Subject<number>();

  lightningIconMapping: any = {
    '5min_record': 'lightning-blue.svg',
    '10min_record': 'lightning-green.svg',
    '15min_record': 'lightning-red.svg'
  }
  forecastLength: any;
  stateBoundary: any;
  selectedView: any;
  selectedLocType:any;
  private _nowcastMouseOver: any = null;
  private _nowcastMouseOut: any = null;

  constructor(
    private http: HttpClient,
    private lightningDashSrv: LightningDashboardService
  ) { }

  resetSessionState() {
    this.selectedLocType = null;
    this.forecastLength = null;
    this.selectedView = null;
    this.hourlyTimeBlocks = [];
    this.selectedHourlyTimestamp = null;
    this.observedHeatMapDate = null;
    this.cachedObservedHeatMapData = null;
  }

  async getSrcWiseLayers(gisMapComp: any) {
    gisMapComp.legendList = {};
    gisMapComp.showThrobber = true;
    let queryParams: any = gisMapComp['params'];
    gisMapComp.getAndSetBaseLayers(3);
    gisMapComp.map.closePopup()
    if (!isNullOrUndefined(queryParams) && Object.keys(queryParams).length > 0) {
      let layerList: any = {};
      let layerName: any;
      let hie = LOCATION_HIE;
      this.forecastLength = queryParams.lightningView == 'observed' ? null : this.forecastLength
      layerName = queryParams.locType != 'BLOCK' ? gisMapComp.gisUtils.titleCase(hie[hie.indexOf(queryParams.locType) + 1]) : gisMapComp.gisUtils.titleCase(queryParams.locType);
      layerList[layerName + " Boundary"] = LIGHTNING_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA][layerName + " Boundary"];
      layerList[layerName + " Boundary Labels"] = LIGHTNING_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA][layerName + " Boundary Labels"];
      if (queryParams.locType != 'COUNTRY') {
        layerList[gisMapComp.gisUtils.titleCase(queryParams.locType)] = LIGHTNING_LAYER_LIST[AgricultureMapConstants.LOCATIONS.INDIA][gisMapComp.gisUtils.titleCase(queryParams.locType) + " Boundaries"];
        layerList = gisMapComp.gisdataService.addMaskLayer(layerList, queryParams, gisMapComp.params.component);
      }

      this.addSrcWiseGeoJSONLayers(gisMapComp, queryParams, layerList);
      this.addSrcWiseVectorLayers(layerList, gisMapComp, queryParams);
      this.addSrcWiseWMSLayers(layerList, gisMapComp, queryParams);

      if (queryParams.lightningView === 'forecast') {
        await this.addLightningHeatMap(gisMapComp, queryParams);
        this.fetchMiniChartData(queryParams);
        this.addLegend(gisMapComp);
      } else {
        await this.addObservedLightningHeatMap(gisMapComp, queryParams)
        this.fetchMiniChartData(queryParams);
        this.addLegend(gisMapComp);
      }
      gisMapComp.otherOptions['onLocationClick'] = queryParams.locType == 'BLOCK' ? false : true;

    }
  }

  // ─── Heatmap (Forecast) ───────────────────────────────────────────────────

  async addLightningHeatMap(gisMapComp: any, queryParams: any) {
    const forecastLength = queryParams.forecastLength;
    if (this.forecastLength === forecastLength && this.selectedView === queryParams.lightningView) {
      gisMapComp.showThrobber = false;
      return;
    }
    this.forecastLength = forecastLength;
    this.selectedView = queryParams.lightningView
    this.gisMapComp = gisMapComp;
    this.lightningHeatMapData = null;
    gisMapComp.labelStrings = [];
    gisMapComp.showThrobber = true;
    await this.getHeatMapLayers(queryParams);
    gisMapComp.showThrobber = false;
  }

  async getHeatMapLayers(queryParams: any) {
    const forecastLength = queryParams['forecastLength'];
    let layerList: any[] = [];
    const playbarIndex = localStorage.getItem('playBarIndex')
    if (forecastLength === '24') {
      layerList = layerList.concat(this.createLightning24HeatMapLayers(queryParams));
    } else {
      layerList = layerList.concat(this.createLightning7DaysHeatMapLayers(queryParams));
    }
    await this.addHeatMapLayers(this.gisMapComp, layerList, queryParams);
  }

  createLightning24HeatMapLayers(queryParams: any): any[] {
    const forecastLength = queryParams['forecastLength'];
    const tsarray = this.lightningDashSrv.get24HrsTimeStamps();
    const layers: any[] = [];
    for (const i in tsarray) {
      const ts = tsarray[i];
      const tsKey = String(ts['timeStamp']);
      const layerObj: any = { ...LightningMapConstants.HeatMapOptions };
      layerObj['url'] = ProjConstants.API_URL + LightningMapConstants.HeatMapUrl + tsKey + '/24'
      layerObj['groupName'] = tsarray[i]['timeStamp'].toString();
      layerObj['groupName'] = tsKey;
      layerObj['setDefault'] = i === '0' ? 'true' : 'false';
      layerObj['time'] = forecastLength;
      layerObj['timeLineName'] = ts['timeLineName'];
      layerObj['timeLinePos'] = ts['timeLinePos'];
      layerObj['leftSideName'] = ts['leftSideName'];
      layerObj['layerName'] = 'Lightning Heatmap';
      layers.push(layerObj);
    }
    return layers;
  }

  createLightning7DaysHeatMapLayers(queryParams: any): any[] {
    const forecastLength = queryParams['forecastLength'];
    const tsarray = this.lightningDashSrv.get7DaysTimeStamps();
    const layers: any[] = [];
    for (const i in tsarray) {
      const ts = tsarray[i];
      const tsKey = String(ts['timeStamp']);
      const layerObj: any = { ...LightningMapConstants.HeatMapOptions };
      layerObj['url'] = ProjConstants.API_URL + LightningMapConstants.HeatMapUrl + tsKey + '/10'
      layerObj['groupName'] = tsKey;
      layerObj['setDefault'] = i === '0' ? 'true' : 'false';
      layerObj['time'] = forecastLength;
      layerObj['timeLineName'] = ts['timeLineName'];
      layerObj['timeLinePos'] = ts['timeLinePos'];
      layerObj['leftSideName'] = ts['leftSideName'];
      layerObj['layerName'] = 'Lightning Heatmap';
      layers.push(layerObj);
    }
    return layers;
  }

  async addHeatMapLayers(gisMapComp: any, layerList: any, queryParams: any) {
    const labels: any[] = [];
    this.removeHeatMapLayers(gisMapComp);
    gisMapComp.labelStrings = [];
    for (const key in layerList) {
      let layer = layerList[key];
      let layerObj: any = await this.createHeatMapLayer(layer, gisMapComp);
      if (!isNullOrUndefined(layerObj['layer'])) {
        if (layer['time'] == queryParams['forecastLength']) {
          labels[layer['timeLinePos']] = {
            groupName: layer['groupName'],
            label: layer['timeLineName'],
            fullLabel: layer['leftSideName'],
            type: 'forecast'
          };
          gisMapComp.layers.heatmaplayers[key] = layerObj;
          gisMapComp.activeLayers[key] = layerObj;
        }
        if (layerObj['time'] == queryParams['forecastLength'] && layerObj['setDefault'] == 'true' && !gisMapComp.map.hasLayer(layerObj['layer'])) {
          gisMapComp['map'].addLayer(layerObj['layer']);
          gisMapComp.activeGroup = layer['groupName'];
        }
      }
    }
    gisMapComp.labelStrings = labels;
    if (labels.length > 0 && labels[0]) {
      gisMapComp.initialCounter = parseInt(gisMapComp.labelStrings[0]['groupName'], 10);
    } else {
      gisMapComp.activeGroup = null;
    }
  }
// fetchMiniChartData(queryParams: any) {
//     const isBlock = queryParams.locType === 'BLOCK';
//     const distUUID = isBlock ? this.getParentUUID(queryParams, 'DISTRICT') : queryParams.pUUID;
//     const pType = isBlock ? 'DISTRICT' : queryParams.locType;
//     const cType = LOCATION_HIE[LOCATION_HIE.indexOf(pType) + 1] || 'BLOCK';

//     if (queryParams.lightningView === 'observed') {
//       const payload = {
//         pUUID: distUUID,
//         pType,
//         cType,
//         source: 'IITM',
//         date: this.lightningDashSrv.getCurrentDateYyyymmdd()
//       };
//       this.lightningDashSrv.getLightningObservedChartData(payload).subscribe((data: any) => {
//         if (!data) { this.miniChartData = null; return; }
//         let filtered = data;
//         if (isBlock) {
//           const blockOnly = Object.keys(data).reduce((acc: any, key) => {
//             if (key === queryParams.pUUID) acc[key] = data[key];
//             return acc;
//           }, {});
//           if (Object.keys(blockOnly).length > 0) filtered = blockOnly;
//         }
//         this.miniChartData = this.lightningDashSrv.transformObservedDataForMiniChart(filtered);
//       }, () => { this.miniChartData = null; });
//       return;
//     }

//     const payload = {
//       sUUID: distUUID,
//       pUUID: distUUID,
//       pType,
//       locationType: cType,
//       view: queryParams.view || 'Admin',
//       forecastLength: queryParams.forecastLength || '24',
//       colorPallette: 'DEFAULT',
//       refTs: this.lightningDashSrv.getRefTimestamp()
//     };
//     this.lightningDashSrv.getLightningChartData(payload).subscribe((data: any) => {
//       if (!data) { this.miniChartData = null; return; }
//       if (isBlock) {
//         const blockUUID = queryParams.pUUID;
//         const filtered: any = {};
//         Object.keys(data).forEach(cat => {
//           if (data[cat] && data[cat][blockUUID] !== undefined) {
//             filtered[cat] = { [blockUUID]: data[cat][blockUUID] };
//           }
//         });
//         this.miniChartData = Object.keys(filtered).length > 0 ? filtered : data;
//       } else {
//         this.miniChartData = data;
//       }
//     }, () => { this.miniChartData = null; });
//   }


  fetchMiniChartData(queryParams: any) {
    const isBlock = queryParams.locType === 'BLOCK';
    const pUUID = isBlock ? this.getParentUUID(queryParams, 'DISTRICT') : queryParams.pUUID;
    const pType = isBlock ? 'DISTRICT' : queryParams.locType;
    const isObserved = queryParams.lightningView === 'observed';
    const cType = pType === 'DISTRICT' ? 'DISTRICT' : (LOCATION_HIE[LOCATION_HIE.indexOf(pType) + 1] || 'BLOCK');

    const applyBlockFilter = (data: any): any => {
      if (!isBlock) { return data; }
      const blockOnly = Object.keys(data).reduce((acc: any, key) => {
        if (key === queryParams.pUUID) { acc[key] = data[key]; }
        return acc;
      }, {});
      return Object.keys(blockOnly).length > 0 ? blockOnly : data;
    };

    if (queryParams.lightningView === 'observed') {
      const payload: any = { pUUID, pType, cType, source: 'IITM', date: this.lightningDashSrv.getCurrentDateYyyymmdd() };
      if (this.selectedHourlyTimestamp) {
        payload.startTs = String(this.selectedHourlyTimestamp);
        payload.endTs = String(this.selectedHourlyTimestamp + 55 * 60 * 1000);
      }
      this.lightningDashSrv.getLightningAggregatedData(payload).subscribe((data: any) => {
        if (!data || !Object.keys(data).length) { this.miniChartData = null; return; }
        this.miniChartData = this.lightningDashSrv.transformAggregatedDataForMiniChart(applyBlockFilter(data), this.miniChartDataField);
      }, () => { this.miniChartData = null; });
      return;
    }

    const is24h = (queryParams.forecastLength || '24') === '24';
    const source = is24h ? 'ECMWF_1HR_HOURLY' : 'ECMWF_1HR';
    this.lightningDashSrv.getLatestLightningForecastFile().subscribe((fileData: any) => {
      const date = fileData ? fileData[source] : null;
      if (!date) { this.miniChartData = null; return; }
      const payload = { pUUID, pType, cType, source, date };
      this.lightningDashSrv.getForecastLightningAggregatedData(payload).subscribe((data: any) => {
        if (!data || !Object.keys(data).length) { this.miniChartData = null; return; }
        this.miniChartData = this.lightningDashSrv.transformAggregatedDataForMiniChart(applyBlockFilter(data), this.miniChartDataField);
      }, () => { this.miniChartData = null; });
    }, () => { this.miniChartData = null; });
  }

  private getParentUUID(queryParams: any, locType: string): string {
    const idx = LOCATION_HIE.indexOf(locType);
    const parts = (queryParams.location || '').split('&');
    return (parts[idx] || '').split('##')[1] || null;
  }

  createHeatMapLayer(layer: any, gis: any) {
    return new Promise((resolve) => {
      const options: any = { opacity: 0.8, pane: 'heatMap', minZoom: 6, maxZoom: 15 };
      this.getShapeBoundaryLayers(layer.view, layer.clip).then(
        (data: any) => {
          if (data != null) {
            options['clip'] = data;
          }
          if (layer.url != null && layer.url != undefined) {
            if (layer.url.startsWith("data:image")) {
              const anchors = layer.imageBounds; // already available

              layer["layer"] = L.imageTransform(layer.url, anchors, options);
              layer['layer'].addTo(gis.map);
              gis.map.removeLayer(layer['layer']);
              resolve(layer);
              return;
            }
            this.lightningDashSrv.getHeatMap(layer.url).then(
              (data: any) => {
                if (data['url'] != null && data['url'] != undefined) {
                  const anchors = this.lightningDashSrv.getImageBounds(data['bounds']);
                  layer['layer'] = L.imageTransform(data['url'], anchors, options);
                  layer['layer'].addTo(gis.map);
                  gis.map.removeLayer(layer['layer']);
                }
                resolve(layer);
              });
          } else {
            resolve(layer);
          }
        })
    });
  }

  getShapeBoundaryLayers(view: any, clip?: any) {
    const shapeBoundary = new Promise((resolve, reject) => {
      if (clip == false) {
        resolve(null);
      }
      if (view == 'Admin') {
        if (this.stateBoundary == null || this.stateBoundary == undefined) {
          const layerName = 'odisha' + ':' + ProjConstants.STATE_TO_LAYERTYPE_NAME['INDIA_BOUNDARY'];
          const boundaryURL = MapConstants.GEOJSON_BOUNDARY_LAYER_URL + '&typeName=' + layerName;
          this.http.get(boundaryURL).subscribe(
            (data: any) => {
              this.stateBoundary = data['features'][0]['geometry'];
              resolve(this.stateBoundary);
            });
        } else {
          resolve(this.stateBoundary);
        }
      }
    });
    return shapeBoundary;
  }

  removeHeatMapLayers(gisMapComp: any) {
    for (const key in gisMapComp.layers.heatmaplayers) {
      if (gisMapComp.map.hasLayer(gisMapComp.layers.heatmaplayers[key]['layer'])) {
        gisMapComp.map.removeLayer(gisMapComp.layers.heatmaplayers[key]['layer']);
      }
      delete gisMapComp.layers.heatmaplayers[key];
    }
  }

  changeTimeLayers(gis: any, groupName: any) {
    if (groupName === gis.activeGroup) return;
    for (const key in gis.layers.heatmaplayers) {
      const layerObj = gis.layers.heatmaplayers[key];
      if (!layerObj || !layerObj['layer']) continue;
      if (layerObj['groupName'] === gis.activeGroup) {
        if (gis.map.hasLayer(layerObj['layer'])) gis.map.removeLayer(layerObj['layer']);
      }
    }
    gis.activeGroup = groupName;
    for (const key in gis.layers.heatmaplayers) {
      const layerObj = gis.layers.heatmaplayers[key];
      if (!layerObj || !layerObj['layer']) continue;
      if (layerObj['groupName'] === groupName) {
        if (!gis.map.hasLayer(layerObj['layer'])) {
          gis.map.addLayer(layerObj['layer']);
        }
      }
    }
  }

  // ─── Heatmap (Observed) ───────────────────────────────────────────────────

  // ── OLD: single-date observed heatmap (commented out — replaced by time-block flow) ──
  // async addObservedLightningHeatMap(gisMapComp: any, queryParams: any) {
  //   gisMapComp.labelStrings = [];
  //   const date = this.lightningDashSrv.getCurrentDateYyyymmdd();
  //   if (this.observedHeatMapDate === date && gisMapComp.labelStrings && gisMapComp.labelStrings.length > 0) {
  //     gisMapComp.showThrobber = false;
  //     return;
  //   }
  //   this.observedHeatMapDate = date;
  //   this.gisMapComp = gisMapComp;
  //   this.removeHeatMapLayers(gisMapComp);
  //   gisMapComp.showThrobber = true;
  //   try {
  //     const data: any = await this.lightningDashSrv.getObservedLightningHeatMapData(date).toPromise();
  //     this.loadObservedHeatMapLayers(gisMapComp, queryParams, data);
  //   } catch (e) {
  //     gisMapComp.showThrobber = false;
  //   }
  // }

  // async loadObservedHeatMapLayers(gisMapComp: any, queryParams: any, data: any) {
  //   const labels: any[] = [];
  //   const timeKeysList = Object.keys(data).sort((a, b) => +a - +b);
  //   const latestTsIndex = timeKeysList.length - 1;
  //   this.removeHeatMapLayers(gisMapComp);
  //   gisMapComp.labelStrings = [];
  //   const clip = await this.getShapeBoundaryLayers('Admin');
  //   const baseOptions: any = { opacity: 0.8, pane: 'heatMap', minZoom: 6, maxZoom: 15 };
  //   if (clip) baseOptions['clip'] = clip;
  //   for (let i = 0; i < timeKeysList.length; i++) {
  //     const tsKey = timeKeysList[i];
  //     const block = data[tsKey];
  //     if (!block || !block.heatMapImage) continue;
  //     const boundSrc = block.heatMapImageBound || { southWest: { latitude: 5, longitude: 65 }, northEast: { latitude: 40, longitude: 100 } };
  //     const anchors = this.lightningDashSrv.getImageBounds(this.lightningDashSrv.getHeatMapImageBounds(boundSrc));
  //     const layer = (L as any).imageTransform(block.heatMapImage, anchors, { ...baseOptions });
  //     const layerObj: any = { layer, groupName: tsKey, setDefault: i === latestTsIndex ? 'true' : 'false', time: 'observed', layerName: 'Lightning Observed Heatmap' };
  //     gisMapComp.layers.heatmaplayers[i] = layerObj;
  //     gisMapComp.activeLayers[i] = layerObj;
  //     labels[i] = { groupName: tsKey, label: { label: this.lightningDashSrv.convertTimestampToBlockValue(+tsKey), toolTip: this.lightningDashSrv.convertTimestampToFullBlockValue(+tsKey) }, fullLabel: this.lightningDashSrv.convertTimestampToFullBlockValue(+tsKey), type: 'observed' };
  //     if (i === latestTsIndex) { gisMapComp.map.addLayer(layer); gisMapComp.activeGroup = tsKey; }
  //   }
  //   gisMapComp.labelStrings = labels;
  //   if (labels.length > 0 && labels[latestTsIndex]) { gisMapComp.initialCounter = parseInt(labels[latestTsIndex].groupName, 10); }
  //   gisMapComp.showThrobber = false;
  // }

  // ── NEW: time-block based observed heatmap ────────────────────────────────
  // Uses the existing all-day heatmap API; filters to the selected hour locally.
  // The range-based API (getLightningHeatMapData/IITM/{date}/{start}/{end}) is
  // preserved in LightningDashboardService for future use once the backend exposes it.

  async addObservedLightningHeatMap(gisMapComp: any, queryParams: any) {
    gisMapComp.labelStrings = [];
    this.gisMapComp = gisMapComp;
    this.removeHeatMapLayers(gisMapComp);
    gisMapComp.showThrobber = true;

    const date = this.lightningDashSrv.getCurrentDateYyyymmdd();

    // Re-use same-day cache: skip re-fetch, just re-render the current hour's layers
    if (
      this.hourlyTimeBlocks.length > 0 &&
      this.observedHeatMapDate === date &&
      this.cachedObservedHeatMapData
    ) {
      const ts = this.selectedHourlyTimestamp || this.hourlyTimeBlocks[0];
      await this.loadHeatMapLayersForHour(gisMapComp, ts);
      return;
    }

    this.observedHeatMapDate = date;
    try {
      // Fetch time blocks first to determine the active timestamp window
      const blocks: number[] = await this.lightningDashSrv.getTimeBlocks().toPromise();

      if (!blocks || !blocks.length) { gisMapComp.showThrobber = false; return; }

      // Sort descending: latest (largest) timestamp first
      this.hourlyTimeBlocks = [...blocks].sort((a, b) => b - a);
      this.selectedHourlyTimestamp = this.hourlyTimeBlocks[0];

      // Fetch heatmap data for the latest 55-minute window using the range API
      const startTs = this.selectedHourlyTimestamp;
      const endTs = startTs + 55 * 60 * 1000;
      const heatMapData: any = await this.lightningDashSrv.getObservedLightningHeatMapDataWithRange(date, startTs, endTs).toPromise();
      this.cachedObservedHeatMapData = heatMapData || {};

      await this.loadHeatMapLayersForHour(gisMapComp, this.selectedHourlyTimestamp);
    } catch (e) {
      gisMapComp.showThrobber = false;
    }
  }

  async loadHeatMapLayersForHour(gisMapComp: any, hourlyTs: number) {
    this.selectedHourlyTimestamp = hourlyTs;
    this.removeHeatMapLayers(gisMapComp);
    gisMapComp.showThrobber = true;

    // Refresh mini chart data for the newly selected hour
    if (gisMapComp.params) {
      this.fetchMiniChartData(gisMapComp.params);
    }

    // Filter cached data for the 55-minute window at this hour block
    const hourEndTs = hourlyTs + 3600000;
    const allData = this.cachedObservedHeatMapData || {};
    const filteredData: any = {};
    Object.keys(allData).forEach(tsKey => {
      const ts = +tsKey;
      if (ts >= hourlyTs && ts < hourEndTs) {
        filteredData[tsKey] = allData[tsKey];
      }
    });

    if (Object.keys(filteredData).length > 0) {
      await this.processHourBlockHeatMapData(gisMapComp, filteredData);
      return;
    }

    // Cache miss: fetch the 55-minute window for this hour block on-demand
    try {
      const date = this.lightningDashSrv.getCurrentDateYyyymmdd();
      const rangeEndTs = hourlyTs + 55 * 60 * 1000;
      const rangeData: any = await this.lightningDashSrv.getObservedLightningHeatMapDataWithRange(date, hourlyTs, rangeEndTs).toPromise();
      Object.assign(this.cachedObservedHeatMapData, rangeData || {});
      await this.processHourBlockHeatMapData(gisMapComp, rangeData || {});
    } catch (e) {
      gisMapComp.showThrobber = false;
    }
  }

  private async processHourBlockHeatMapData(gisMapComp: any, data: any) {
    if (!data || !Object.keys(data).length) { gisMapComp.showThrobber = false; return; }

    const labels: any[] = [];
    const timeKeysList = Object.keys(data).sort((a, b) => +a - +b);
    const latestTsIndex = timeKeysList.length - 1;

    const clip = await this.getShapeBoundaryLayers('Admin');
    const baseOptions: any = { opacity: 0.8, pane: 'heatMap', minZoom: 6, maxZoom: 15 };
    if (clip) baseOptions['clip'] = clip;

    for (let i = 0; i < timeKeysList.length; i++) {
      const tsKey = timeKeysList[i];
      const block = data[tsKey];
      if (!block || !block.heatMapImage) continue;

      const boundSrc = block.heatMapImageBound || {
        southWest: { latitude: 5, longitude: 65 },
        northEast: { latitude: 40, longitude: 100 }
      };
      const anchors = this.lightningDashSrv.getImageBounds(
        this.lightningDashSrv.getHeatMapImageBounds(boundSrc)
      );
      const layer = (L as any).imageTransform(block.heatMapImage, anchors, { ...baseOptions });

      const layerObj: any = {
        layer,
        groupName: tsKey,
        setDefault: i === latestTsIndex ? 'true' : 'false',
        time: 'observed',
        layerName: 'Lightning Observed Heatmap'
      };
      gisMapComp.layers.heatmaplayers[i] = layerObj;
      gisMapComp.activeLayers[i] = layerObj;

      labels[i] = {
        groupName: tsKey,
        label: {
          label: this.lightningDashSrv.convertTimestampToBlockValue(+tsKey),
          toolTip: this.lightningDashSrv.convertTimestampToFullBlockValue(+tsKey)
        },
        fullLabel: this.lightningDashSrv.convertTimestampToFullBlockValue(+tsKey),
        type: 'observed'
      };

      if (i === latestTsIndex) {
        gisMapComp.map.addLayer(layer);
        gisMapComp.activeGroup = tsKey;
      }
    }

    gisMapComp.labelStrings = labels;
    if (labels.length > 0 && labels[latestTsIndex]) {
      gisMapComp.initialCounter = parseInt(labels[latestTsIndex].groupName, 10);
    }
    gisMapComp.showThrobber = false;
  }

  // async processObservedHeatMapData(gisMapComp: any, data: any) {
  //   const labels: any[] = [];
  //   const sortedKeys = Object.keys(data).sort((a, b) => +a - +b);
  //   const clip = await this.getShapeBoundaryLayers('Admin');
  //   const baseOptions: any = { opacity: 0.8, pane: 'heatMap', minZoom: 6, maxZoom: 15 };
  //   if (clip) baseOptions['clip'] = clip;

  //   for (let i = 0; i < sortedKeys.length; i++) {
  //     const tsKey = sortedKeys[i];
  //     const block = data[tsKey];
  //     if (!block || !block.heatMapImage || !block.heatMapImageBound) continue;

  //     const imageBounds = this.lightningDashSrv.getHeatMapImageBounds(block.heatMapImageBound);
  //     const anchors = this.lightningDashSrv.getImageBounds(imageBounds);
  //     const layer = (L as any).imageTransform(block.heatMapImage, anchors, { ...baseOptions });
  //     layer.addTo(gisMapComp.map);
  //     gisMapComp.map.removeLayer(layer);

  //     const layerObj: any = {
  //       layer,
  //       groupName: tsKey,
  //       setDefault: i === 0 ? 'true' : 'false',
  //       time: 'observed',
  //       layerName: 'Lightning Observed Heatmap'
  //     };
  //     gisMapComp.layers.heatmaplayers[i] = layerObj;
  //     gisMapComp.activeLayers[i] = layerObj;

  //     labels[i] = {
  //       groupName: tsKey,
  //       label: { label: this.lightningDashSrv.convertTimestampToBlockValue(+tsKey), toolTip: this.lightningDashSrv.convertTimestampToFullBlockValue(+tsKey) },
  //       fullLabel: this.lightningDashSrv.convertTimestampToFullBlockValue(+tsKey),
  //       type: 'observed'
  //     };

  //     if (i === 0) {
  //       gisMapComp.map.addLayer(layer);
  //       gisMapComp.activeGroup = tsKey;
  //     }
  //   }

  //   gisMapComp.labelStrings = labels;
  //   if (labels.length > 0 && labels[0]) {
  //     gisMapComp.initialCounter = parseInt(labels[0].groupName, 10);
  //   } else {
  //     gisMapComp.activeGroup = null;
  //   }
  //   gisMapComp.showThrobber = false;
  // }

  // ─── Layer helpers ────────────────────────────────────────────────────────

  addSrcWiseGeoJSONLayers(gisMapComp: any, queryParams:any, layerList: any) {
    if (this.selectedLocType == queryParams.locType) return
    this.selectedLocType = queryParams.locType
    let geoJsonLayersBasedOnView = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.GEOJSON_LAYER);
    geoJsonLayersBasedOnView = this.formatLayersData(gisMapComp, geoJsonLayersBasedOnView);
    gisMapComp.addSrcWiseGEOJSONLayers(geoJsonLayersBasedOnView);
  }

  formatLayersData(gisMapComp: any, layerList: any) {
    for (let layerName in layerList) {
      if (layerList[layerName].layerOptions.cql_filter_value) {
        layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + gisMapComp.params["pUUID"] + "'";
      }
    }
    return layerList;
  }

  async addSrcWiseVectorLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let vectorLayersBasedOnView = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.VECTOR_LAYER);
    vectorLayersBasedOnView = this.addSourceWiseLayers(gisMapComp, vectorLayersBasedOnView);
    for (let layer in layerList) {
      if (layerList[layer].applycql_filter) {
        vectorLayersBasedOnView = this.formatVectorLayersData(vectorLayersBasedOnView, queryParams, gisMapComp);
      }
    }
    this.setVectorLayerStyles(vectorLayersBasedOnView, gisMapComp);
    gisMapComp.addSrcWiseVECTORLayers(vectorLayersBasedOnView);
  }

  formatVectorLayersData(layerList: any, queryParams?: any, gisMapComp?: any) {
    if (!isNullOrUndefined(queryParams)) {
      for (let layerName in layerList) {
        let uuid: any = queryParams["pUUID"];
        if (queryParams.locType == "COUNTRY" && layerName == "State Boundary") {
          layerList[layerName].layerOptions.cql_filter_value = null;
          layerList[layerName].layerOptions.cql_filter = "1=1";
        }
        if (queryParams.locType == "COUNTRY" && layerName == "District Boundary") {
          layerList[layerName].layerOptions.cql_filter_value = null;
          layerList[layerName].layerOptions.cql_filter = "1=1";
        }
        if (queryParams["locType"] == "STATE" && layerName == "District Boundary") {
          layerList[layerName].layerOptions.cql_filter_value = "state_uuid";
          layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + uuid + "'";
        }
        if (queryParams["locType"] == "DISTRICT" && layerName == "Block Boundary") {
          layerList[layerName].layerOptions.cql_filter_value = "dist_uuid";
          layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + uuid + "'";
        }
        if (queryParams["locType"] == "BLOCK" && layerName == "Block Boundary") {
          layerList[layerName].layerOptions.cql_filter_value = "uuid";
          layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + uuid + "'";
        }
      }
      return layerList;
    }
  }

  setVectorLayerStyles(layerList: any, gisMapComp: any) {
    for (let key in layerList) {
      let layerName = layerList[key]["layerOptions"]["layerName"].split(":")[1];
      layerList[key].layerOptions.vectorTileLayerStyles[layerName] = function (properties: any) {
        let hie: any = AgricultureMapConstants.ADMIN_BOUNDARIES_HIE;
        let levelBoundary = gisMapComp.gisUtils.titleCase(gisMapComp.params.locType) + " Boundaries";
        gisMapComp.map.eachLayer((layer: any) => {
          if (layer.options.layer_name === levelBoundary) {
            if (gisMapComp.map.hasLayer(layer)) {
              gisMapComp.map.removeLayer(layer);
            }
          }
        });
        return {
          color: "#000000",
          fill: false, 
          weight: gisMapComp.params.locType == 'COUNTRY' ? 0.8 : 0.3, 
          opacity: 0.8,
          fillColor: 'transparent',
          fillOpacity: 0.5
        };
      };
    }
  }

  async addSrcWiseWMSLayers(layerList: any, gisMapComp: any, queryParams: any) {
    let wmsLayersBasedOnView = gisMapComp.gisdataService.getApplicationBasedLayer(layerList, LAYER_TYPES.WMS_LAYER);
    wmsLayersBasedOnView = this.addSourceWiseLayers(gisMapComp, wmsLayersBasedOnView);
    for (let layer in layerList) {
      if (layerList[layer].applycql_filter) {
        wmsLayersBasedOnView = this.formatWmsLayersData(wmsLayersBasedOnView, queryParams);
      }
    }
    gisMapComp.addSrcWiseWMSLayers(wmsLayersBasedOnView);
  }

  addSourceWiseLayers(gisMapComp: any, layerList?: any) {
    let layers: any = {};
    for (var i in layerList) {
      layers[i] = layerList[i];
      gisMapComp.activeLayers[i] = layerList[i].layerOptions.visible;
    }
    return layers;
  }

  formatWmsLayersData(layerList: any, queryParams?: any) {
    if (!isNullOrUndefined(queryParams)) {
      for (let layerName in layerList) {
        let uuid: any = queryParams.pUUID;
        if (queryParams.locType == "STATE") {
          let allLocations = queryParams.location.split("&");
          let stateUUID = allLocations[1].split("##")[1];
          if (layerList[layerName].layerOptions.cql_filter_value) {
            layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + stateUUID + "'";
          }
        }
        if (queryParams.locType == "DISTRICT") {
          let allLocations = queryParams.location.split("&");
          let districtUUID = allLocations[2].split("##")[1];
          if (layerList[layerName].layerOptions.cql_filter_value) {
            layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + districtUUID + "'";
          }
        }
        if (queryParams.locType == 'BLOCK') {
          let allLocations = queryParams.location.split('&')
          let districtUUID = allLocations[3].split('##')[1]
          if (layerList[layerName].layerOptions.cql_filter_value) {
            layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + districtUUID + "'";
          }
          if (layerName == 'Block Boundary Labels') {
            if (layerList[layerName].layerOptions.cql_filter_value) {
              layerList[layerName].layerOptions.cql_filter_value = 'uuid'
              layerList[layerName].layerOptions.cql_filter = layerList[layerName].layerOptions.cql_filter_value + "='" + queryParams.pUUID + "'";
            }
          }
        }
        if (layerName.includes("world_view")) {
          if (!isNullOrUndefined(queryParams.type)) {
            layerList[layerName].layerOptions.cql_filter = queryParams.locType == "VILLAGE" ? "1=1;village_uuid" + "='" + uuid + "'" : "1=1;" + AgricultureMapConstants.LOCTYPE_CQLFILTERVALUE[queryParams.locType] + "='" + uuid + "'";
          } else {
            layerList[layerName].layerOptions.cql_filter = "1=1;" + AgricultureMapConstants.LOCTYPE_CQLFILTERVALUE[queryParams.locType] + "='" + uuid + "'";
          }
        }
      }
      return layerList;
    }
  }

  addLegend(gisMapComp: any) {
    const legendSrc = LIGHTNING_FORECAST_LEGEND_LIST[AgricultureMapConstants.LOCATIONS.INDIA];
    for (const key in legendSrc) {
      gisMapComp['legendList'][key] = legendSrc[key];
    }
    gisMapComp.manageLegend();
    gisMapComp.gisUtils.setupLegendTooltip(this._nowcastMouseOver, this._nowcastMouseOut, 'rf-legend-tooltip');
  }

  handleAdminPolygons(layer: any, mapObj: any, params: any, gisMapComp: any) {
    layer.setStyle({ color: "#000", fill: false, weight: 1.5 });
  }

  // ─── Observed markers ────────────────────────────────────────────────────

  async setLightningDataOnMap(gisMapComp: any, queryParams: any) {
    return new Promise((resolve, reject) => {
      this.getLightningData().subscribe(data => {
        if (data) {
          resolve(data['lightning_data']);
        }
      }, err => {
        resolve(null);
      });
    });
  }

  getLightningData() {
    return this.http.get<any>('http://103.251.184.43/json/new_lightning_file.php');
  }

  removePreviousMarkerLayer(gisMapComp: any) {
    this.markerLayer.eachLayer((layer: any) => {
      if (!isNullOrUndefined(layer) && gisMapComp.map.hasLayer(layer)) {
        gisMapComp.map.removeLayer(layer);
      }
    });
  }

  addLightningMarkersOnMap(gisMapComp: any, data: any, queryParams?: any) {
    if (isNullOrUndefined(data)) return;
    this.removePreviousMarkerLayer(gisMapComp);
    this.markerLayer = new L.LayerGroup();
    const filter = queryParams && queryParams.lightningFilter ? queryParams.lightningFilter : 'all';
    const keysToRender = filter === 'all' ? Object.keys(data) : [filter];
    keysToRender.forEach(key => {
      const categoryData = data[key];
      if (categoryData && categoryData.length > 0) {
        for (let i in categoryData) {
          let item = categoryData[i];
          if (!isNullOrUndefined(item['latitude']) && !isNullOrUndefined(item['longitude'])) {
            let latLng = [item['latitude'], item['longitude']];
            const iconName = this.lightningIconMapping[key];
            const iconUrl = `assets/imgs/` + iconName;
            const pointIcon = L.divIcon({
              html: `<div><span class="alert-icon"><img src="${iconUrl}"/></span></div>`,
              iconSize: [4, 4],
              iconAnchor: [6, 6],
              popupAnchor: [0, -6],
            });
            let popupContent = this.setHtmlContentForLightning(item);
            let marker = L.marker(latLng, { icon: pointIcon, pane: 'imagePoints' });
            this.markerLayer.addLayer(marker);
            marker.on('click', function () {
              const popup = R.responsivePopup({ className: 'alert-popup', hasTip: false, autoPan: false, pane: 'max', offset: [1, 2], minWidth: 360, maxWidth: 450, closeButton: true })
                .setLatLng(latLng)
                .setContent(popupContent)
                .openOn(gisMapComp.map);
            });
            marker.on('mouseout', function () {
              marker.closePopup();
            });
          }
        }
      }
    });
    gisMapComp.map.addLayer(this.markerLayer);
  }

  renderMarkersForFilter(gisMapComp: any, filter: string) {
    this.addLightningMarkersOnMap(gisMapComp, this.cachedLightningData, { lightningFilter: filter });
  }

  setHtmlContentForLightning(properties: any) {
    var htmlString = "<html><body>";
    htmlString += '<div class="alert-poupup-table">';
    htmlString += `<div class="card-body p-0">
       <table class="table table-sm m-0 table-bordered">`;
    htmlString += `<tbody>
        <tr><th>Flash Type</th><td>${properties['flash_type']}</td></tr>
        <tr><th>IC Height</th><td>${properties['ic_height']}</td></tr>
        <tr><th>Number of Sensors</th><td>${properties['number_of_sensors']}</td></tr>
        <tr><th>Peak Current</th><td>${properties['peak_current']}</td></tr>
        <tr><th>Time</th><td>${properties['time']}</td></tr>
     </tbody></table></div></div>`;
    return htmlString;
  }
}
